import {Component, HostListener, OnDestroy} from '@angular/core';
import {UserService} from '@services/user.service';
import {AppConfigService} from '@services/app-config.service';
import {configVariableValues} from '@shared/constants/config-variable-values.constants';
import {Subscription} from 'rxjs';
import {Router} from '@angular/router';
import {LoginService} from '@services/login.service';

@Component({
  selector: 'si-user-inactivity',
  templateUrl: './user-inactivity.component.html',
  styleUrls: ['./user-inactivity.component.scss']
})
export class UserInactivityComponent implements OnDestroy {
  inactivityTimeout;
  inactivityTimeoutVariable: number;
  inactivityDialogSubscription: Subscription;
  displayDialogInactivity = false;
  countdownTimeRemaining: number;
  inactivityCountdownInterval: number;
  isCountdownRunning = false;
  isSessionExpired = false;

  constructor(
    private userService: UserService,
    private appConfigService: AppConfigService,
    private router: Router,
    private loginService: LoginService
  ) {
    this.inactivityTimeoutVariable = parseInt(this.appConfigService.getVariableValue(configVariableValues.INACTIVE_SESSION_TIMEOUT), 10);
  }

  @HostListener('window:mousemove')
  @HostListener('window:touchstart')
  resetTimer(): void {
    this.userService.setInactivityFlag(false);
    clearTimeout(this.inactivityTimeout);
    this.inactivityTimeout = setTimeout(() => {
      this.userService.setInactivityFlag(true);
    }, this.inactivityTimeoutVariable);
  }

  ngOnInit(): void {
    this.resetTimer();
    this.initInactivityDialog();
  }

  initInactivityDialog(): void {
    this.userService.initDialogInactivity();
    this.inactivityDialogSubscription = this.userService.getDialogInactivityObservable().subscribe(displayDialog => {
      if (displayDialog) {
        if (new Date().getTime() > this.loginService.getRefreshTimestamp()) {
          this.onFinishInactivityCountdown();
        } else {
          this.isSessionExpired = false;
          this.startInactivityCountdown();
        }
      }

      this.displayDialogInactivity = displayDialog;
    });
  }

  startInactivityCountdown(): void {
    this.stopInactivityCountdown();
    this.isCountdownRunning = true;
    this.countdownTimeRemaining = this.getCountdownTimeRemaining();
    this.inactivityCountdownInterval = setInterval(() => {
      if (!this.isCountdownRunning) {
        this.stopInactivityCountdown();
        return;
      }

      this.countdownTimeRemaining = this.getCountdownTimeRemaining();
      if (this.countdownTimeRemaining <= 0) {
        this.countdownTimeRemaining = 0;
        this.onFinishInactivityCountdown();
      }
    }, 1000);
  }

  stopInactivityCountdown(): void {
    if (this.inactivityCountdownInterval) {
      clearInterval(this.inactivityCountdownInterval);
      this.inactivityCountdownInterval = null;
    }
  }

  getCountdownTimeRemaining(): number {
    const refreshTimestamp = this.loginService.getRefreshTimestamp();
    if (!refreshTimestamp) {
      return 0;
    }

    return Math.floor((refreshTimestamp - new Date().getTime()) / 1000);
  }

  async onFinishInactivityCountdown(): Promise<void> {
    this.stopInactivityCountdown();
    this.isSessionExpired = true;
    await this.loginService.revoke(false);
  }

  onClickCloseDialogInactivity(acceptFlag: boolean): void {
    this.isCountdownRunning = false;
    this.userService.closeDialogInactivity(acceptFlag);
  }

  onClickGoToLogin(): void {
    this.userService.closeDialogInactivity(false, false);
    this.router.navigate(['/login']);
  }

  ngOnDestroy(): void {
    if (this.inactivityDialogSubscription) {
      this.inactivityDialogSubscription.unsubscribe();
    }

    clearTimeout(this.inactivityTimeout);
    this.stopInactivityCountdown();
  }
}
